import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
	threshold?: number;
	rootMargin?: string;
	triggerOnce?: boolean;
}

/**
 * Hook for scroll reveal animations
 * Detects when element enters viewport
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
	options: UseScrollRevealOptions = {}
) {
	const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
	const ref = useRef<T>(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					if (triggerOnce) {
						observer.unobserve(element);
					}
				} else if (!triggerOnce) {
					setIsVisible(false);
				}
			},
			{ threshold, rootMargin }
		);

		observer.observe(element);

		return () => {
			observer.unobserve(element);
		};
	}, [threshold, rootMargin, triggerOnce]);

	return { ref, isVisible };
}

/**
 * Hook for multiple elements scroll reveal
 */
export function useScrollRevealMultiple(
	count: number,
	options: UseScrollRevealOptions = {}
) {
	const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
	const refs = useRef<(HTMLElement | null)[]>([]);
	const [visibleItems, setVisibleItems] = useState<boolean[]>(
		new Array(count).fill(false)
	);

	useEffect(() => {
		const observers: IntersectionObserver[] = [];

		refs.current.forEach((element, index) => {
			if (!element) return;

			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) {
						setVisibleItems((prev) => {
							const next = [...prev];
							next[index] = true;
							return next;
						});
						if (triggerOnce) {
							observer.unobserve(element);
						}
					} else if (!triggerOnce) {
						setVisibleItems((prev) => {
							const next = [...prev];
							next[index] = false;
							return next;
						});
					}
				},
				{ threshold, rootMargin }
			);

			observer.observe(element);
			observers.push(observer);
		});

		return () => {
			observers.forEach((observer) => observer.disconnect());
		};
	}, [count, threshold, rootMargin, triggerOnce]);

	const setRef = (index: number) => (el: HTMLElement | null) => {
		refs.current[index] = el;
	};

	return { setRef, visibleItems };
}

export default useScrollReveal;




